<template>
    <div role="table" class="w-full">
        <div v-if="header" role="row"
            class="mb-1 hidden items-center gap-4 border-b border-white/[0.06] px-3 pb-2 text-xs text-nomad-400 sm:grid"
            :class="likes ? 'grid-cols-[2rem_minmax(0,1fr)_2.5rem_4rem]' : 'grid-cols-[2rem_minmax(0,1fr)_4rem]'">
            <span class="text-center">#</span>
            <span>Title</span>
            <span v-if="likes"></span>
            <span class="icon-clock text-right" aria-label="Duration"></span>
        </div>
        <template v-for="(song, index) in songs" :key="song.id">
        <div role="row"
            class="group grid items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-white/[0.05] sm:gap-4 sm:px-3"
            :class="[{ 'bg-white/[0.04]': isCurrent(song) }, likes ? 'grid-cols-[1.5rem_minmax(0,1fr)_2.5rem_auto] sm:grid-cols-[2rem_minmax(0,1fr)_2.5rem_4rem]' : 'grid-cols-[1.5rem_minmax(0,1fr)_auto] sm:grid-cols-[2rem_minmax(0,1fr)_4rem]']" @dblclick="play(song)">
            <!-- Index / play -->
            <div class="relative flex h-8 items-center justify-center text-sm tabular-nums text-nomad-400">
                <span v-if="isCurrent(song) && isPlaying" class="nl-eq group-hover:hidden"><span></span><span></span><span></span></span>
                <span v-else class="group-hover:hidden" :class="{ 'text-nomad-accent': isCurrent(song) }">{{ index + 1 }}</span>
                <button type="button" @click="play(song)" :aria-label="'Play ' + titleOf(song)"
                    class="absolute inset-0 hidden items-center justify-center text-base text-white group-hover:flex">
                    <span :class="isCurrent(song) && isPlaying ? 'icon-pause' : 'icon-play'" aria-hidden="true"></span>
                </button>
            </div>

            <!-- Title -->
            <div class="flex min-w-0 items-center gap-3">
                <img v-if="artwork && artworkOf(song)" :src="artworkOf(song)" alt="" loading="lazy"
                    class="h-10 w-10 shrink-0 rounded object-cover" />
                <div class="min-w-0">
                    <Link :href="'/song/' + song.id" class="block truncate text-[0.9375rem] hover:underline"
                        :class="isCurrent(song) ? 'text-nomad-accent' : 'text-white'">
                        {{ titleOf(song) }}
                    </Link>
                    <p v-if="song.artist" class="truncate text-sm text-nomad-400">
                        <Link :href="'/artist/' + song.artist.id" class="hover:text-white hover:underline">{{ song.artist.name }}</Link>
                        <template v-if="song.featuring && song.featuring.length">,
                            <template v-for="(feat, i) in song.featuring" :key="feat.id">
                                <Link :href="'/artist/' + feat.id" class="hover:text-white hover:underline">{{ feat.name }}</Link><span v-if="i < song.featuring.length - 1">, </span>
                            </template>
                        </template>
                    </p>
                </div>
            </div>

            <button v-if="likes" type="button" @click="$store.dispatch('favorites/toggleFavorite', song)"
                class="flex h-10 w-10 items-center justify-center rounded-full text-lg text-nomad-accent transition-colors hover:bg-white/[0.06]"
                :aria-label="'Remove ' + titleOf(song) + ' from liked songs'" title="Remove from liked songs">
                <span class="icon-heart" aria-hidden="true"></span>
            </button>

            <!-- Duration -->
            <span class="text-right text-sm tabular-nums text-nomad-400">{{ durationOf(song) }}</span>
        </div>
        <AdSlot v-if="adAfter.includes(index) && index < songs.length - 1" placement="listFeed" class="my-6" />
        </template>
    </div>
</template>
<script>
import { Link } from "@inertiajs/vue3";
import AdSlot from "@/components/ui/AdSlot.vue";
export default {
    name: "TrackList",
    components: { Link, AdSlot },
    props: {
        songs: { type: Array, default: () => [] },
        artwork: { type: Boolean, default: true },
        header: { type: Boolean, default: false },
        // Show a heart on each row that removes the song from liked songs
        likes: { type: Boolean, default: false },
        // Row indexes to show an in-feed ad after (only when more rows follow)
        adAfter: { type: Array, default: () => [] },
    },
    computed: {
        currentSong() {
            return this.$store.getters["current/currentSong"] || {};
        },
        isPlaying() {
            return this.$store.getters["current/isPlaying"];
        },
    },
    methods: {
        isCurrent(song) {
            return this.currentSong.id === song.id;
        },
        titleOf(song) {
            return song.title || song.name;
        },
        artworkOf(song) {
            return song.artwork || song.meta?.image || song.cover || song.artist?.image;
        },
        durationOf(song) {
            // formatted_duration looks like "3m:21s"; show it as 3:21
            const raw = song.formatted_duration;
            if (!raw) return "";
            const match = String(raw).match(/(?:(\d+)m)?:?(?:(\d+)s)?/);
            if (!match || (!match[1] && !match[2])) return raw;
            return `${match[1] || 0}:${String(match[2] || 0).padStart(2, "0")}`;
        },
        play(song) {
            if (this.isCurrent(song)) {
                // Same track: pause/resume instead of reloading it
                this.$store.commit("current/REQUEST_TOGGLE");
                return;
            }
            this.$store.dispatch("current/prepareCurrent", {
                id: song.id,
                type: "SONG",
                name: this.titleOf(song),
            });
        },
    },
};
</script>
